import { useState, useEffect, useCallback } from "react";
import { activityLogAPI } from "../lib/api";
import { toast } from "sonner";
import {
  Activity,
  User,
  Calendar,
  Filter,
  RefreshCw,
  FileText,
  Users,
  Package,
  Building2,
  Receipt,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";

// Icons for entity types
const ENTITY_ICONS = {
  customer: Users,
  offer: FileText,
  invoice: Receipt,
  product: Package,
  project: ClipboardList,
  user: User,
  company: Building2
};

// Colors for action types
const ACTION_COLORS = {
  customer_created: "bg-green-100 text-green-800",
  customer_updated: "bg-blue-100 text-blue-800",
  customer_deleted: "bg-red-100 text-red-800",
  offer_created: "bg-green-100 text-green-800",
  offer_signed: "bg-purple-100 text-purple-800",
  offer_accepted: "bg-emerald-100 text-emerald-800",
  offer_rejected: "bg-red-100 text-red-800",
  free_offer_created: "bg-amber-100 text-amber-800",
  invoice_created: "bg-green-100 text-green-800",
  invoice_finalized: "bg-blue-100 text-blue-800",
  invoice_cancelled: "bg-red-100 text-red-800",
  invoice_paid: "bg-emerald-100 text-emerald-800",
  free_invoice_created: "bg-amber-100 text-amber-800",
  free_invoice_finalized: "bg-amber-100 text-amber-800",
  product_created: "bg-green-100 text-green-800",
  product_updated: "bg-blue-100 text-blue-800",
  user_login: "bg-gray-100 text-gray-800",
  default: "bg-gray-100 text-gray-800"
};

export default function ActivityLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  
  // Filters
  const [entityType, setEntityType] = useState("all");
  const [userId, setUserId] = useState("all");
  const [days, setDays] = useState("7");
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const loadLogs = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const params = {
        limit,
        offset,
        days: parseInt(days)
      };
      
      if (entityType !== "all") params.entity_type = entityType;
      if (userId !== "all") params.user_id = userId;
      
      const response = await activityLogAPI.list(params);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to load logs:", error);
      toast.error("Fehler beim Laden des Protokolls");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [offset, entityType, userId, days]);

  const loadStats = async () => {
    try {
      const response = await activityLogAPI.stats(parseInt(days));
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await activityLogAPI.users();
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  useEffect(() => {
    loadLogs();
    loadStats();
    loadUsers();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    loadStats();
  }, [days]);

  const formatDateTime = (isoDate) => {
    if (!isoDate) return "-";
    const date = new Date(isoDate);
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatTime = (isoDate) => {
    if (!isoDate) return "-";
    const date = new Date(isoDate);
    return date.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    const date = new Date(isoDate);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const getEntityIcon = (entityType) => {
    const Icon = ENTITY_ICONS[entityType] || Activity;
    return <Icon className="h-4 w-4" />;
  };

  const getActionColor = (action) => {
    return ACTION_COLORS[action] || ACTION_COLORS.default;
  };

  const handleRefresh = () => {
    loadLogs(true);
    loadStats();
  };

  const handleFilterChange = () => {
    setOffset(0);
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Aktivitätsprotokoll</h1>
          <p className="text-muted-foreground">
            Alle Benutzeraktionen der letzten {days} Tage
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh} 
          disabled={refreshing}
          data-testid="refresh-log-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Gesamt Aktionen</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Activity className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          
          {stats.by_user?.slice(0, 3).map((user, idx) => (
            <Card key={user._id || idx}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{user.user_name || "Unbekannt"}</p>
                    <p className="text-2xl font-bold">{user.count}</p>
                  </div>
                  <User className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="h-5 w-5" />
            Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Zeitraum</label>
              <Select 
                value={days} 
                onValueChange={(v) => { setDays(v); handleFilterChange(); }}
              >
                <SelectTrigger data-testid="days-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Letzter Tag</SelectItem>
                  <SelectItem value="3">Letzte 3 Tage</SelectItem>
                  <SelectItem value="7">Letzte 7 Tage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Bereich</label>
              <Select 
                value={entityType} 
                onValueChange={(v) => { setEntityType(v); handleFilterChange(); }}
              >
                <SelectTrigger data-testid="entity-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Bereiche</SelectItem>
                  <SelectItem value="customer">Kunden</SelectItem>
                  <SelectItem value="offer">Angebote</SelectItem>
                  <SelectItem value="invoice">Rechnungen</SelectItem>
                  <SelectItem value="product">Produkte</SelectItem>
                  <SelectItem value="project">Projekte</SelectItem>
                  <SelectItem value="user">Benutzer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Benutzer</label>
              <Select 
                value={userId} 
                onValueChange={(v) => { setUserId(v); handleFilterChange(); }}
              >
                <SelectTrigger data-testid="user-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Benutzer</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <p className="text-sm text-muted-foreground">
                {total} Einträge gefunden
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Log Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5" />
            Protokolleinträge
          </CardTitle>
          <CardDescription>
            Chronologische Auflistung aller Aktivitäten
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Zeitpunkt</TableHead>
                <TableHead className="w-[150px]">Benutzer</TableHead>
                <TableHead>Aktion</TableHead>
                <TableHead>Objekt</TableHead>
                <TableHead className="hidden lg:table-cell">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Keine Aktivitäten im gewählten Zeitraum
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-medium text-sm">{formatDate(log.timestamp)}</p>
                        <p className="text-xs text-muted-foreground">{formatTime(log.timestamp)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{log.user_name || "Unbekannt"}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[120px]">
                            {log.user_email}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getActionColor(log.action)}>
                        {log.action_label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">
                          {getEntityIcon(log.entity_type)}
                        </span>
                        <span className="text-sm">{log.entity_name || "-"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-sm text-muted-foreground">
                        {log.details || "-"}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <p className="text-sm text-muted-foreground">
                Zeige {offset + 1} - {Math.min(offset + limit, total)} von {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Zurück
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                >
                  Weiter
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
